import {
  ConfigurableRoleOption,
  DEFAULT_PROJECT_MANAGER_ROLES,
  getProjectManagerRoleLabel,
  isProjectManagerLeadRole,
} from "resource-planner-model";
import { useSelector } from "react-redux";
import { AppState } from "../redux/AppTypes";

export interface ProjectManagerRoleOption {
  id: string;
  label: string;
  countsAsProjectManagerLead?: boolean;
}

interface ProjectManagerRoleSource {
  role?: string;
}

const applicationConfigSelector = (state: AppState) =>
  state.applicationConfig?.item || {
    projectManagerRoles: DEFAULT_PROJECT_MANAGER_ROLES,
  };

const emsSelector = (state: AppState): ProjectManagerRoleSource[] =>
  Object.values(state.ems?.items || {}) as ProjectManagerRoleSource[];

function findConfiguredRole(
  roleId: string,
  projectManagerRoles: ConfigurableRoleOption[]
): ConfigurableRoleOption | undefined {
  return projectManagerRoles.find(
    (role) =>
      role.id === roleId ||
      role.label === roleId ||
      (role.legacyValues || []).includes(roleId)
  );
}

export function buildProjectManagerRoleOptions(
  projectManagerRoles: ConfigurableRoleOption[] = DEFAULT_PROJECT_MANAGER_ROLES,
  ems: ProjectManagerRoleSource[] = [],
  extraRoleIds: string[] = []
): ProjectManagerRoleOption[] {
  const optionsById = new Map<string, ProjectManagerRoleOption>();

  function addOption(id: string, role?: ConfigurableRoleOption): void {
    if (!id || optionsById.has(id)) {
      return;
    }
    optionsById.set(id, {
      id,
      label: role?.label || getProjectManagerRoleLabel(id, projectManagerRoles),
      countsAsProjectManagerLead: role
        ? isProjectManagerLeadRole(id, [role])
        : isProjectManagerLeadRole(id, projectManagerRoles),
    });
  }

  (projectManagerRoles || [])
    .filter((role) => role.id && !role.archived)
    .sort((left, right) => left.order - right.order)
    .forEach((role) => addOption(role.id, role));

  ems.forEach((em) => {
    const configured = em.role
      ? findConfiguredRole(em.role, projectManagerRoles)
      : undefined;
    addOption(em.role || "", configured);
  });
  extraRoleIds.forEach((roleId) => {
    const configured = findConfiguredRole(roleId, projectManagerRoles);
    addOption(roleId, configured);
  });

  return Array.from(optionsById.values());
}

export function getProjectManagerRoleOptionLabel(
  options: ProjectManagerRoleOption[],
  roleId?: string
): string {
  if (!roleId) {
    return "no role assigned";
  }
  return options.find((option) => option.id === roleId)?.label || roleId;
}

export function isProjectManagerLeadOption(
  options: ProjectManagerRoleOption[],
  roleId?: string
): boolean {
  return !!options.find((option) => option.id === roleId)
    ?.countsAsProjectManagerLead;
}

export function useProjectManagerRoles(
  extraRoleIds: string[] = []
): ProjectManagerRoleOption[] {
  const config = useSelector(applicationConfigSelector);
  const ems = useSelector(emsSelector);
  return buildProjectManagerRoleOptions(
    config.projectManagerRoles,
    ems,
    extraRoleIds
  );
}
